import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useToast } from "../contexts/ToastContext";
import {
  getAdminSubscriptionDetails,
  getAdminSubscriptionHistory,
  changeSubscriptionStatus,
  createManualSubscription,
  registerBankTransferPayment,
  createBillingOperation,
  listBillingOperations,
  sendBillingOperationInvoice,
  updateBillingPeriod,
  switchToBankTransfer,
  createAdminProforma,
} from "../services/adminSubscriptionsApi";
import {
  ChangeSubscriptionStatusDto,
  CreateManualSubscriptionDto,
  RegisterBankTransferPaymentDto,
  CreateBillingOperationDto,
  UpdateBillingPeriodDto,
  SwitchToBankTransferDto,
  CreateAdminProformaDto,
  CreateAdminProformaResponse,
} from "../types/adminSubscriptions";
import { SETTLEMENT_KEYS } from "./useKiedyNaborySettlements";

const ADMIN_SUBSCRIPTION_KEYS = {
  details: (tenantId: string) => ["admin-subscription", tenantId],
  history: (tenantId: string, limit: number, offset: number) => [
    "admin-subscription-history",
    tenantId,
    limit,
    offset,
  ],
  billingOperations: (tenantId: string, limit: number, offset: number) => [
    "billing-operations",
    tenantId,
    limit,
    offset,
  ],
};

const getMonthKey = (dateString: string): string => {
  const date = new Date(dateString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  return `${year}-${month}`;
};

export const useAdminSubscriptionDetailsQuery = (
  tenantId?: string,
  options?: { refetchOnWindowFocus?: boolean }
) => {
  return useQuery({
    queryKey: ADMIN_SUBSCRIPTION_KEYS.details(tenantId || "unknown"),
    queryFn: () => getAdminSubscriptionDetails(tenantId as string),
    enabled: !!tenantId,
    refetchOnWindowFocus: options?.refetchOnWindowFocus,
  });
};

export const useAdminSubscriptionHistoryQuery = (
  tenantId?: string,
  limit: number = 10,
  offset: number = 0
) => {
  return useQuery({
    queryKey: ADMIN_SUBSCRIPTION_KEYS.history(
      tenantId || "unknown",
      limit,
      offset
    ),
    queryFn: () =>
      getAdminSubscriptionHistory(tenantId as string, { limit, offset }),
    enabled: !!tenantId,
  });
};

export const useChangeSubscriptionStatusMutation = (tenantId: string) => {
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  return useMutation({
    mutationFn: (dto: ChangeSubscriptionStatusDto) =>
      changeSubscriptionStatus(tenantId, dto),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ADMIN_SUBSCRIPTION_KEYS.details(tenantId),
      });
      queryClient.invalidateQueries({
        queryKey: ["admin-subscription-history", tenantId],
      });
      queryClient.invalidateQueries({ queryKey: SETTLEMENT_KEYS.customers() });

      addToast({
        message: "Status subskrypcji został zaktualizowany",
        type: "success",
      });
    },
    onError: (error: any) => {
      addToast({
        message:
          error.response?.data?.message ||
          "Nie udało się zaktualizować statusu",
        type: "error",
      });
    },
  });
};

export const useCreateManualSubscriptionMutation = (tenantId: string) => {
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  return useMutation({
    mutationFn: (dto: CreateManualSubscriptionDto) =>
      createManualSubscription(tenantId, dto),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ADMIN_SUBSCRIPTION_KEYS.details(tenantId),
      });
      queryClient.invalidateQueries({
        queryKey: SETTLEMENT_KEYS.customers(),
      });

      addToast({
        message: "Subskrypcja została utworzona",
        type: "success",
      });
    },
    onError: (error: any) => {
      addToast({
        message:
          error.response?.data?.message ||
          "Nie udało się utworzyć subskrypcji",
        type: "error",
      });
    },
  });
};

export const useRegisterBankTransferPaymentMutation = () => {
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  return useMutation({
    mutationFn: (dto: RegisterBankTransferPaymentDto) =>
      registerBankTransferPayment(dto),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ADMIN_SUBSCRIPTION_KEYS.details(variables.tenantId),
      });
      queryClient.invalidateQueries({
        queryKey: SETTLEMENT_KEYS.customers(),
      });

      const monthKey = getMonthKey(variables.paymentDate);
      queryClient.invalidateQueries({
        queryKey: SETTLEMENT_KEYS.monthly(monthKey),
      });

      addToast({
        message: "Płatność przelewowa została dodana",
        type: "success",
      });
    },
    onError: (error: any) => {
      addToast({
        message:
          error.response?.data?.message ||
          "Nie udało się dodać płatności przelewowej",
        type: "error",
      });
    },
  });
};

export const useUpdateBillingPeriodMutation = (tenantId: string) => {
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  return useMutation({
    mutationFn: (dto: UpdateBillingPeriodDto) =>
      updateBillingPeriod(tenantId, dto),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ADMIN_SUBSCRIPTION_KEYS.details(tenantId),
      });
      queryClient.invalidateQueries({
        queryKey: ["admin-subscription-history", tenantId],
      });
      queryClient.invalidateQueries({
        queryKey: SETTLEMENT_KEYS.customers(),
      });

      addToast({
        message: "Okres rozliczeniowy został zaktualizowany",
        type: "success",
      });
    },
    onError: (error: any) => {
      addToast({
        message:
          error.response?.data?.message ||
          "Nie udało się zaktualizować okresu rozliczeniowego",
        type: "error",
      });
    },
  });
};

/**
 * Switches a Stripe-managed subscription to bank transfer billing, which unlocks
 * the billing period dropdown and, with it, migration to Billing BC.
 * @see docs/specifications/2026-09-02-switch-stripe-subscription-to-bank-transfer/spec.md
 */
export const useSwitchToBankTransferMutation = (tenantId: string) => {
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  return useMutation({
    mutationFn: (dto: SwitchToBankTransferDto) =>
      switchToBankTransfer(tenantId, dto),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ADMIN_SUBSCRIPTION_KEYS.details(tenantId),
      });
      queryClient.invalidateQueries({
        queryKey: ["admin-subscription-history", tenantId],
      });
      queryClient.invalidateQueries({
        queryKey: SETTLEMENT_KEYS.customers(),
      });

      addToast({
        message:
          "Subskrypcja przełączona na płatność przelewem — możesz ustawić okres rozliczeniowy",
        type: "success",
      });
    },
    onError: (error: any) => {
      addToast({
        message:
          error.response?.data?.message ||
          "Nie udało się przełączyć subskrypcji na przelew",
        type: "error",
      });
    },
  });
};

// =====================================================
// Billing Operations (Manual Billing Flow)
// =====================================================

export const useBillingOperationsQuery = (
  tenantId?: string,
  limit: number = 10,
  offset: number = 0
) => {
  return useQuery({
    queryKey: ADMIN_SUBSCRIPTION_KEYS.billingOperations(
      tenantId || "unknown",
      limit,
      offset
    ),
    queryFn: () => listBillingOperations(tenantId as string, { limit, offset }),
    enabled: !!tenantId,
  });
};

export const useCreateBillingOperationMutation = (tenantId: string) => {
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  return useMutation({
    mutationFn: (dto: CreateBillingOperationDto) =>
      createBillingOperation(tenantId, dto),
    onSuccess: (result) => {
      // Invalidate all related queries
      queryClient.invalidateQueries({
        queryKey: ADMIN_SUBSCRIPTION_KEYS.details(tenantId),
      });
      queryClient.invalidateQueries({
        queryKey: ["admin-subscription-history", tenantId],
      });
      queryClient.invalidateQueries({
        queryKey: ["billing-operations", tenantId],
      });
      queryClient.invalidateQueries({
        queryKey: SETTLEMENT_KEYS.customers(),
      });

      let message = "Operacja billingowa została zapisana";
      if (result.invoice) {
        message += `. Faktura: ${result.invoice.number}`;
      }
      if (result.emailSent) {
        message += ". Email wysłany.";
      }

      addToast({
        message,
        type: "success",
      });
    },
    onError: (error: any) => {
      addToast({
        message:
          error.response?.data?.message ||
          "Nie udało się utworzyć operacji billingowej",
        type: "error",
      });
    },
  });
};

export const useSendBillingOperationInvoiceMutation = (tenantId: string) => {
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  return useMutation({
    mutationFn: (operationId: string) =>
      sendBillingOperationInvoice(operationId, tenantId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["billing-operations", tenantId],
      });

      addToast({
        message: "Faktura została wysłana",
        type: "success",
      });
    },
    onError: (error: any) => {
      addToast({
        message:
          error.response?.data?.message || "Nie udało się wysłać faktury",
        type: "error",
      });
    },
  });
};

// =====================================================
// Admin Proforma Generator
// =====================================================

/**
 * Hook to create admin proforma for a tenant
 *
 * @param tenantId - UUID of the tenant
 * @returns Mutation for creating proforma
 * @see docs/specifications/2026-02-18-admin-proforma-generator.md
 */
export const useCreateAdminProformaMutation = (tenantId: string) => {
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  return useMutation<CreateAdminProformaResponse, Error, CreateAdminProformaDto>({
    mutationFn: (dto) => createAdminProforma(tenantId, dto),
    onSuccess: (result, variables) => {
      // Invalidate related queries
      queryClient.invalidateQueries({
        queryKey: ADMIN_SUBSCRIPTION_KEYS.details(tenantId),
      });
      queryClient.invalidateQueries({
        queryKey: SETTLEMENT_KEYS.customers(),
      });

      const message = variables.sendEmail !== false
        ? `Proforma ${result.proformaNumber} zostala wystawiona i wyslana`
        : `Proforma ${result.proformaNumber} zostala wystawiona`;

      addToast({
        message,
        type: "success",
      });
    },
    onError: (error: any) => {
      const errorMessage =
        error.response?.data?.message || "Nie udalo sie wystawic proformy";
      addToast({
        message: errorMessage,
        type: "error",
      });
    },
  });
};
